
import { Action, Getter, namespace } from "vuex-class"
import { Component, Vue, Watch, Prop } from "vue-property-decorator"

import { TimeManagementSettings, UserFilterField } from "@/types/timeManagement"
import { Field, DataSourceItem } from "@/types"

import vSelect from "vue-select"
import FieldControl from "@/components/Fields/FieldControl.vue"
import { IdLabelPair } from "@/types/labeledValue"

@Component({
	components: {
		"v-select": vSelect,
		"field-control": FieldControl
	}
})
export default class UserExcludeFromAvailableSetup extends Vue {
	@Prop({ required: true })
	value!: { [userFieldId: string]: string[] }

	@Prop({ required: true })
	listTypeUserFields!: Field[]

	@Prop({ required: true })
	listTypeUserFieldsOptions!: { [userFieldId: string]: DataSourceItem[] }

	private addingField = false
	private showFieldControls = true

	private columns = [
		{
			key: "userField",
			class: "w-20"
		},
		{
			key: "excludeWithValues"
		},
		{
			key: "controls",
			label: "",
			class: "w-5"
		}
	]

	private mounted () {
		if (!this.value) {
			this.$emit("input", {})
		}

		// Remove any User Fields that have been removed from the User Object Definition
		const removedUserFieldIds = this.tableItems.filter(item => typeof item.userField === "string")
			.map(item => item.userField as string)

		if (removedUserFieldIds.length > 0) {
			const newValue = { ...this.value }
			removedUserFieldIds.forEach(fieldId => delete newValue[fieldId])
			this.$emit("input", newValue)
		}
	}

	private setUserFieldValue (item: { userField?: { id: string } }, userFieldId: string) {
		const updated = {
			...this.value,
			[userFieldId]: this.value[userFieldId] ?? []
		}

		if (item?.userField?.id && updated[item.userField.id]) {
			delete updated[item.userField.id]
		}

		this.addingField = false

		this.$emit("input", updated)
	}

	private async remove (userFieldId: string) {
		const newValue = { ...this.value }
		delete newValue[userFieldId]

		// don't render field controls during removal to avoid a blank value bug with that component
		this.showFieldControls = false
		await this.$nextTick()

		this.$emit("input", newValue)
		this.showFieldControls = true
	}

	private get availableListTypeUserFields () {
		return this.listTypeUserFields.filter(f => !Object.keys(this.value).includes(f.id!))
	}

	private get tableItems (): { userField?: Field | string | undefined, excludeWithValues: string[], controls: null }[] {
		const valueItems = Object.keys(this.value ?? {}).map(userFieldId => {
			const userField = this.listTypeUserFields.find(f => f.id === userFieldId)
			return {
				userField: userField ?? userFieldId,
				excludeWithValues: this.value[userFieldId],
				controls: null
			}
		}) as { userField?: Field | string | undefined, excludeWithValues: string[], controls: null }[]

		if (this.addingField) {
			valueItems.push({
				userField: undefined,
				excludeWithValues: [],
				controls: null
			})
		}

		return valueItems
	}

	private fieldExcludeValuesChanged (definitionFieldId: string, newValue: IdLabelPair[]) {
		this.$emit("input", {
			...this.value,
			[definitionFieldId]: newValue.map(v => v.id)
		})
	}

	private get fieldControlValue (): { [userFieldId: string]: IdLabelPair[] } {
		return Object.keys(this.value ?? {}).reduce((acc, userFieldId) => {
			const userField = this.listTypeUserFields.find(f => f.id === userFieldId)
			if (userField) {
				acc[userFieldId] = this.value[userFieldId].map(v => {
					const option = this.listTypeUserFieldsOptions[userFieldId]?.find(o => o.id === v)
					return {
						id: v,
						label: option?.label ?? v
					}
				})
			}
			return acc
		}, {} as { [userFieldId: string]: IdLabelPair[] })
	}
}
